import styled, { css } from 'styled-components';
import { baseVariables, toRem } from '@marriott/mi-ui-library';
import { theme } from '@marriott/mi-ui-library-shop';

// Mobile-only top margin clears space for the image that extends above the card (V1/V2). Using margin
// (not padding) keeps authored `pt-*` on the same node: padding applies at every breakpoint while this
// margin only applies below md. From md up, default `pt-md-5` handles top spacing when not authored.

export const StyledMerchandisingSmallBannerUpgraded = styled.div<{ $hasImageOverhangMargin: boolean }>`
  ${({ $hasImageOverhangMargin }) =>
    $hasImageOverhangMargin &&
    css`
      margin-top: ${toRem(56)};
      @media ${baseVariables.mediaQuery.md} {
        margin-top: 0;
      }
    `}

  .skeleton-loader-composite {
    background-color: #d2d2d2;
    color: white;
    -webkit-animation: fadein 2s ease-in alternate infinite;
    -moz-animation: fadein 2s ease-in alternate infinite;
    animation: fadein 2s ease-in alternate infinite;
    border-radius: 8px;
  }
  @-webkit-keyframes fadein {
    10% {
      opacity: 0.3;
    }
    25% {
      opacity: 0.35;
    }
    50% {
      opacity: 0.4;
    }
    75% {
      opacity: 0.5;
    }
    100% {
      opacity: 0.6;
    }
  }
  @-moz-keyframes fadein {
    10% {
      opacity: 0.3;
    }
    25% {
      opacity: 0.35;
    }
    50% {
      opacity: 0.4;
    }
    75% {
      opacity: 0.5;
    }
    100% {
      opacity: 0.6;
    }
  }
  @keyframes fadein {
    10% {
      opacity: 0.3;
    }
    25% {
      opacity: 0.35;
    }
    50% {
      opacity: 0.4;
    }
    75% {
      opacity: 0.5;
    }
    100% {
      opacity: 0.6;
    }
  }
  .merchandising-small-bannerv3 {
    border: ${baseVariables.border['borderWidth02']} solid ${baseVariables.color['neutral20']};
    border-radius: ${baseVariables.border['borderInset02']};
    box-shadow: ${baseVariables.shadows['shadowSubtle01']};
    overflow: hidden;

    &__image {
      width: ${toRem(220)};
      border-radius: ${toRem(6)};
      &-container {
        position: relative;
        top: ${toRem(0)};
        max-height: ${toRem(140)};
        @media ${baseVariables.mediaQuery.md} {
          top: ${toRem(40)};
          max-height: ${toRem(130)};
        }
        @media ${baseVariables.mediaQuery.lg} {
          top: ${toRem(12)};
          max-height: ${toRem(70)};
        }
        &.loading {
          aspect-ratio: 3/2;
          width: ${toRem(221)};
          @media ${baseVariables.mediaQuery.md} {
            width: ${toRem(221)};
          }
          @media ${baseVariables.mediaQuery.lg} {
            width: ${toRem(225)};
          }
        }
      }
    }
    &__qr-image {
      width: ${toRem(60)};
      margin-left: ${toRem(0)};
      padding-left: ${toRem(0)};
      &.loading {
        height: ${toRem(60)};
      }
    }

    &__button-qr-container {
      @media ${baseVariables.mediaQuery.md} {
        display: flex;
        flex-direction: column;
      }
    }

    &__content {
      &.loading {
        width: 100%;
      }
    }

    &__textContent {
      margin-top: ${toRem(40)};
      &.loading {
        width: 100%;
      }
    }
    &__heading {
      margin-bottom: 0;
      &.loading {
        width: 80%;
      }
    }
    &__description {
      &.loading {
        width: 80%;
        margin-top: ${toRem(12)} !important;
      }
    }
    &__description p {
      margin-bottom: 0;
    }
    //this button styles are only applied at loading phase
    &__button {
      width: ${toRem(127)};
      border-radius: ${toRem(52)};
      @media ${baseVariables.mediaQuery.lg} {
        margin-right: ${toRem(16)} !important;
      }
    }
  }

  .merchandising-small-banner {
    border: ${baseVariables.border['borderWidth02']} solid ${baseVariables.color['neutral20']};
    border-radius: ${baseVariables.border['borderRadiusDefault']};
    box-shadow: ${baseVariables.shadows['shadowSubtle01']};
    padding: 0 0.5rem 2rem;

    @media ${baseVariables.mediaQuery.md} {
      padding: ${toRem(13)} ${toRem(24)};
    }

    @media ${baseVariables.mediaQuery.lg} {
      padding-left: ${toRem(48)};
      padding-right: ${toRem(77)};
    }

    &__image {
      width: ${toRem(163)};

      @media ${baseVariables.mediaQuery.sm} {
        width: ${toRem(230)};
      }

      @media ${baseVariables.mediaQuery.md} {
        width: ${toRem(117.5)};
      }

      &.withVariation {
        width: ${toRem(180)};
      }
      &.withVariation.condensed {
        width: ${toRem(155)};
      }
      @media ${baseVariables.mediaQuery.sm} {
        &.withVariation.condensed {
          width: ${toRem(135)};
        }
      }

      &-container {
        margin-top: -${toRem(56)};

        &.loading {
          aspect-ratio: 3/2;
          width: ${toRem(155)};
          @media ${baseVariables.mediaQuery.md} {
            width: ${toRem(117.5)};
          }
        }
      }
    }
    &__content {
      width: 100%;
    }
    &__description,
    .disclaimer-text {
      p {
        margin: 0;
      }
    }

    &__button {
      width: 100%;
      white-space: nowrap;
      @media ${baseVariables.mediaQuery.md} {
        width: auto;
      }
    }

    &__variations {
      .total-amount {
        color: ${baseVariables.color.merch20};
      }
    }
    &__variations.condensed {
      .t-font-alt-xs,
      .t-font-s,
      .t-font-m {
        color: ${baseVariables.color.base10};
        font-size: ${toRem(12)};
      }
      .t-font-m {
        font-weight: ${baseVariables.font.fontWeightMedium};
      }
      .total-amount {
        color: ${baseVariables.color.merch20};
      }
    }
    &__description.condensed {
      color: ${baseVariables.color.base10};
    }
  }
  .preview-condensed-width {
    max-width: 752px !important;
  }

  .merchandising-small-bannerv2 {
    border: ${baseVariables.border['borderWidth02']} solid ${baseVariables.color['neutral20']};
    box-shadow: ${baseVariables.shadows['shadowSubtle01']};
    border-radius: ${toRem(16)};
    @media ${baseVariables.mediaQuery.md} {
      border-radius: ${baseVariables.border['borderInset02']};
    }
    &.withVariation {
      border-radius: ${toRem(14)};
      @media ${baseVariables.mediaQuery.lg} {
        border-radius: ${toRem(8)};
      }
    }

    &__image {
      width: ${toRem(180)};
      position: relative;
      border-radius: ${toRem(6)};

      @media ${baseVariables.mediaQuery.md} {
        width: ${toRem(136)};
        margin-left: -3.5rem;
      }

      &-container {
        margin-top: -${toRem(56)};
        &.withVariation {
          margin-top: -${toRem(48)};
        }

        &.loading {
          aspect-ratio: 3/2;
          width: ${toRem(155)};

          @media ${baseVariables.mediaQuery.md} {
            width: ${toRem(117.5)};
          }
        }
      }
    }

    .bonus-statement-label {
      @media ${baseVariables.mediaQuery.lg} {
        height: 1rem;
      }
    }

    &__content {
      width: 100%;
    }
    &__border {
      height: ${toRem(86)};
      border: ${toRem(1)} solid ${theme.color['light-gray-3']};
    }
    &__variations {
      .total-amount {
        color: ${baseVariables.color.merch20};
      }
    }
    &__button {
      width: 100%;
      white-space: nowrap;
      @media ${baseVariables.mediaQuery.md} {
        width: auto;
      }

      &-icon {
        height: 1rem;
        width: 1rem;

        &[class*='icon-arrow-']::before {
          position: unset;
        }
      }
    }
  }

  .icon-arrow-left-external::before {
    font-size: 1rem;
    left: 5rem;
}
 
.icon-arrow-right-external::before {
    font-size: 1rem;
    right: 1rem;
}
 
.icon-arrow-left-external:hover::before,
.icon-arrow-left-external.focus-visible::before {
    transform: rotate(45deg);
    transition: 0.15s all ease-in;
}
 
.icon-arrow-right-external:hover::before,
.icon-arrow-right-external.focus-visible::before {
    transform: rotate(-45deg);
    transition: 0.15s all ease-in;
}
`;
